import {
  confirmedEvidenceCopyChatIdsAtom,
  evidenceCopyConfirmationModalAtom,
  IS_EVIDENCE_COPY_CONFIRMATION_ENABLED,
} from '@/atoms/evidenceCopyConfirmationAtoms';
import type { EvidenceCopyConfirmationSurface } from '@/models/analytics/features/evidence';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, type RefObject } from 'react';

type UseEvidenceCopyConfirmationGateParams = {
  /** Chat id used to scope the per-chat confirmation. If null, the gate is skipped. */
  chatId: string | null | undefined;
  /**
   * Backend message ID (msg_*) of the response being copied, when available.
   * Reply actions always own a single response; user-notes blocks own the
   * assistant response that emitted the draft (via extractMessageId). Omit for
   * surfaces with no owning response (e.g. coding-case thread). Flows to the
   * copy analytics event.
   */
  messageId?: string | null;
  /** Which copy button surface this gate is wired to (for analytics segmentation). */
  surface: EvidenceCopyConfirmationSurface;
  /** Ref to the copy button — the popover anchors to whatever element this points at. */
  anchorRef: RefObject<HTMLButtonElement>;
};

/**
 * Gates Evidence copy-to-clipboard actions behind the PHI/PII confirmation popover
 * (EVI-1452 / SWR-88).
 *
 * Behaviour:
 * - `chatId` missing → run the action immediately (nothing to key persistence on).
 * - Chat already confirmed in this device's storage → run the action immediately.
 * - Otherwise → open the singleton popover (anchored to `anchorRef.current`) and
 *   defer the action until the user confirms.
 *
 * Returns two helpers:
 * - `doActionWithConfirmation(action)` — the generic wrapper, for any gated action.
 * - `doCopyWithConfirmation(copy)` — the copy-shaped wrapper. Resolves to whether the
 *   copy actually reached the clipboard, which is what a `CopyButton` call site needs
 *   to decide whether to play its copied animation.
 */
export function useEvidenceCopyConfirmationGate({
  chatId,
  messageId,
  surface,
  anchorRef,
}: UseEvidenceCopyConfirmationGateParams) {
  const setModalState = useSetAtom(evidenceCopyConfirmationModalAtom);
  const confirmedChatIds = useAtomValue(confirmedEvidenceCopyChatIdsAtom);

  const isAlreadyConfirmed = Boolean(chatId && confirmedChatIds[chatId]);

  // Must invoke `action` synchronously on every path that lets it through.
  // `doCopyWithConfirmation` reads the variable this callback assigns as soon as
  // this returns — an `await` in here would make it read `undefined` and report
  // every successful copy as a failure.
  const doActionWithConfirmation = useCallback(
    (action: () => void) => {
      if (!IS_EVIDENCE_COPY_CONFIRMATION_ENABLED) {
        action();
        return;
      }

      const anchorEl = anchorRef.current;

      // Skip the gate if we have no chatId to scope the confirmation, this
      // chat is already confirmed on this device, or there is no live anchor
      // element to attach the popover to. The "no anchor" fallback is defensive
      // — if the button has unmounted between mousedown and click we shouldn't
      // strand the user with an orphan popover.
      if (!chatId || isAlreadyConfirmed || !anchorEl) {
        action();
        return;
      }

      setModalState({
        isOpen: true,
        onConfirm: action,
        chatId,
        messageId: messageId ?? null,
        surface,
        anchorEl,
      });
    },
    [chatId, messageId, isAlreadyConfirmed, surface, anchorRef, setModalState]
  );

  /**
   * Runs `copy` through the gate and resolves to whether it actually happened.
   *
   * The gate invokes its callback synchronously whenever it lets the action
   * through, so an unassigned `copied` means the popover took over and nothing
   * has reached the clipboard yet. Keeping that reasoning here — rather than at
   * each call site — means only this file depends on that timing.
   *
   * That deferred branch has no in-place feedback — deliberately, not by
   * oversight. The copy still runs later from the popover's confirm, but the
   * suppressed animation and the dropped success toast leave the dialog itself as
   * the only acknowledgement. Exercised with the attestation forced on: the
   * clipboard write lands, and for a once-per-chat interruption that reads as
   * confirmation on its own. Re-check that judgement if it is switched back on.
   */
  const doCopyWithConfirmation = useCallback(
    async (copy: () => Promise<boolean>): Promise<boolean> => {
      let copied: Promise<boolean> | undefined;
      doActionWithConfirmation(() => {
        copied = copy();
      });
      return (await copied) ?? false;
    },
    [doActionWithConfirmation]
  );

  return { doActionWithConfirmation, doCopyWithConfirmation };
}
